import type { GraphNode, SocketIn } from "~/js/graph/node-socket-connection";
import { SocketCustomErrorMessageType } from "~/js/graph/socketCustomErrorMessage";
import type { nodeNewExecutor } from "~/js/utilities/changeNodeExecutorOnCustom";
import { Default_Executor_Text, execute } from "~/js/utilities/executor";
import {
	parseStringSizeResolution,
	resizeImageWithCanvas
} from "~/solidJs/shared/helpers/imageHelpers";
import { addCropperToImageSocket, findSocket } from "~/solidJs/shared/helpers/nodeHelpers";
import type { Resolution } from "~/solidJs/shared/helpers/types";
import {
	getImageDimensionsFromFile,
	getRatioFromResolution
} from "~/solidJs/shared/helpers/validateImages";
import { ExecutionStatus, NodeExecutor } from "../../node-executor";

export const sdVideoCustomExecutor: nodeNewExecutor = (node, _layout, _initialExecutor) => {
	const inputImg = node.inputSocketGroup.socketHashMap.get("init_img");
	const resolutionSocket = findSocket("resolution", node);

	const executor = new NodeExecutor(node);
	executor.useEnterExitTriggers();
	executor.setStatusEnabled(true);
	executor.updateStatus(ExecutionStatus.Ready, Default_Executor_Text);
	executor.setExecuteFunction(async (cancelController: AbortController) => {
		const inputImage = await inputImg?.getValue();
		if (!inputImage || !inputImg) {
			executor.updateStatus(
				ExecutionStatus.Error,
				inputImg?.getErrorMessage(SocketCustomErrorMessageType.EmptyNonOptional) ||
					`Input ${inputImg?.layout?.label} is empty`,
				"error_params"
			);
			return;
		}

		const resolution = await resolutionSocket?.getValue();
		if (!resolution || typeof resolution !== "string") {
			executor.updateStatus(
				ExecutionStatus.Error,
				"Please choose the Resolution",
				"error_params"
			);
			return;
		}
		const targetResolution = parseStringSizeResolution(resolution);
		if (!targetResolution) {
			executor.updateStatus(
				ExecutionStatus.Error,
				"Please choose the Resolution",
				"error_params"
			);
			return;
		}
		const imgResolution = await getImageDimensionsFromFile(inputImage);
		if (!imgResolution) {
			executor.updateStatus(
				ExecutionStatus.Error,
				"Input image has unknown format",
				"error_params"
			);
			return;
		}
		const inputRatio = getRatioFromResolution(imgResolution);
		const targetRatio = getRatioFromResolution(targetResolution);
		if (inputRatio !== targetRatio) {
			executor.updateStatus(
				ExecutionStatus.Error,
				"Your image resolution and the chosen resolution don't match. Please resize the image using Downscale, Upscale or Crop node, or choose different resolution",
				"error_params"
			);
			createCropButtonErrorMessage(node, inputImg, targetResolution);
			return;
		}
		const resizedInputImage = await resizeImageWithCanvas(
			inputImage,
			targetResolution.height,
			targetResolution.width
		);
		if (!resizedInputImage) {
			executor.updateStatus(
				ExecutionStatus.Error,
				"Your image resolution and the chosen resolution don't match. Please resize the image using Downscale, Upscale or Crop node, or choose different resolution",
				"error_params"
			);
			return;
		}
		try {
			await execute(node, executor, cancelController, {
				init_img: resizedInputImage
			});
		} catch (e) {
			console.error(e);
		}
	});
};
const createCropButtonErrorMessage = (
	node: GraphNode,
	inputImgSocket: SocketIn,
	resolution: Resolution
) => {
	const createCropBtn = Object.assign(document.createElement("button"), {
		className: "block border-0 bg-red-400 text-white cursor-pointer",
		textContent: "Crop"
	});
	createCropBtn.addEventListener("click", () => {
		node.executuionBlock.updateStatus(ExecutionStatus.Ready, Default_Executor_Text);
		addCropperToImageSocket(node, inputImgSocket, resolution);
	});
	node.executuionBlock.appendStatus(createCropBtn);
};
